import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Track, getTrackFromPlan } from '../utils/track';

interface ImpersonatingInfo {
  userId: string;
  email: string | null;
  name: string;
}

interface UserSubscription {
  status: string;
  plan?: string;
  expiresAt?: string;
}

export interface AuthUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  profileImageUrl: string | null;
  subscription?: UserSubscription;
  isSubscribed?: boolean;
  isAdmin?: boolean;
  role?: string;
  track?: Track;
  impersonating?: ImpersonatingInfo | null;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isSubscribed: boolean;
  isAdmin: boolean;
  impersonating: ImpersonatingInfo | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
  navigateToLogin: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/user', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUser({
          id: data.id,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          profileImageUrl: data.profileImageUrl || null,
          subscription: data.subscription,
          isSubscribed: data.isSubscribed,
          isAdmin: data.isAdmin,
          role: data.role,
          track: data.track || getTrackFromPlan(data.subscription?.plan),
          impersonating: data.impersonating || null,
        });
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email: string, password: string) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Login failed');
    }
    await fetchUser();
  };

  const logout = async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    setUser(null);
  };

  const navigateToLogin = () => {
    window.location.href = '/login';
  };

  const isAuthenticated = !!user;
  const isSubscribed = user?.isSubscribed || false;
  const isAdmin = user?.isAdmin || false;
  const impersonating = user?.impersonating || null;

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated,
      isSubscribed,
      isAdmin,
      impersonating,
      login,
      logout,
      refetch: fetchUser,
      navigateToLogin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
